import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Lock, Plus, Trash2 } from 'lucide-react'
import clsx from 'clsx'

import {
  crmErrorDetail,
  crmListService,
  type CrmContactList,
  type CrmListRule,
  type CrmListVocabulary,
} from '@/services/crm'
import DialogShell from './DialogShell'
import { LIST_COLOURS } from './crmFormat'

/**
 * The rules that decide who is on a list, and a count of who that is.
 *
 * A list somebody maintains by hand goes stale on the first deal that arrives
 * after they stop maintaining it, so a list here is a RULE: `Internal` is
 * "email domain is antarcticacapital.com", and the analyst who joins next
 * week is on it the moment their first message is read.
 *
 * The builder previews as you type. A rule set that selects nobody looks
 * identical to one that has not been saved yet, and the only way to tell them
 * apart is to show the count before it is saved.
 *
 * A built-in list — People, Brokers, Internal — keeps its name: the names are
 * the category vocabulary the whole screen is filed under. Its rules are
 * editable, because a tenant's own definition of "internal" is theirs to set.
 */
const BLANK_RULE: CrmListRule = { field: 'email_domain', operator: 'is', value: '' }

export default function ListEditorDialog({
  list,
  vocabulary,
  onClose,
}: {
  /** Null to create a new list. */
  list: CrmContactList | null
  vocabulary: CrmListVocabulary
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(list?.name ?? '')
  const [description, setDescription] = useState(list?.description ?? '')
  const [match, setMatch] = useState<'all' | 'any'>(list?.match ?? 'all')
  const [colour, setColour] = useState<string | null>(list?.colour ?? null)
  const [rules, setRules] = useState<CrmListRule[]>(list?.rules?.length ? list.rules : [BLANK_RULE])
  const [error, setError] = useState<string | null>(null)
  const isSystem = Boolean(list?.isSystem)

  const needsValue = useMemo(
    () => new Map(vocabulary.operators.map((o) => [o.value, o.needsValue])),
    [vocabulary.operators],
  )

  // Only rules that are actually finished are previewed: a half-typed rule
  // would show "0 contacts" and read as "this rule is wrong".
  const ready = useMemo(
    () => rules.filter((r) => r.field && (!needsValue.get(r.operator) || r.value.trim())),
    [rules, needsValue],
  )
  const readyKey = JSON.stringify(ready)
  const [preview, setPreview] = useState<{ total: number; outOf: number } | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)

  useEffect(() => {
    if (ready.length === 0) {
      setPreview(null)
      return
    }
    let cancelled = false
    setPreviewFailed(false)
    const timer = setTimeout(() => {
      crmListService
        .preview({ match, rules: ready })
        .then((result) => {
          if (!cancelled) setPreview({ total: result.total, outOf: result.outOf })
        })
        .catch(() => {
          if (!cancelled) {
            setPreview(null)
            setPreviewFailed(true)
          }
        })
    }, 350)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // `ready` is a new array on every render; the serialised form is the
    // real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match, readyKey])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        description: description.trim(),
        match,
        rules: ready,
        // A colour is sent when chosen; an empty string clears one the list
        // already had. A list that never had one is not sent a blank.
        ...(colour ? { colour } : list?.colour ? { colour: '' } : {}),
        // A built-in list's name is not sent: the server refuses to rename
        // one, and a name it already has is not a change.
        ...(isSystem ? {} : { name: name.trim() }),
      }
      return list ? crmListService.update(list.id, body) : crmListService.create(body)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] }),
      ])
      onClose()
    },
    onError: (failure) => setError(crmErrorDetail(failure, 'The list could not be saved.')),
  })

  function setRule(index: number, patch: Partial<CrmListRule>) {
    setRules((current) => current.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  const canSave = (isSystem || name.trim().length > 0) && !save.isPending
  const label = list ? `Rules · ${list.name}` : 'New list'

  return (
    <DialogShell label={label} onClose={onClose} width="2xl">
      <form
        className="min-h-0 flex-1 space-y-3 overflow-y-auto scrollbar-thin px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          if (canSave) save.mutate()
        }}
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="list-name" className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-stone-600">
              Name{isSystem ? null : <span className="ml-0.5 text-amber-400">*</span>}
            </label>
            {isSystem ? (
              <div
                id="list-name"
                className="flex items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/20 px-2.5 py-1.5 text-[12px] text-stone-300"
                title="A built-in list keeps its name"
              >
                <Lock size={10} className="text-stone-600" aria-label="Built in" />
                {list?.name}
              </div>
            ) : (
              <input
                id="list-name"
                value={name}
                autoFocus
                onChange={(event) => setName(event.target.value)}
                placeholder="Repeat borrowers"
                className="w-full rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1.5 text-[12px] text-stone-200 outline-none placeholder:text-stone-600 focus:border-amber-400/30"
              />
            )}
          </div>
          <div>
            <label htmlFor="list-description" className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-stone-600">
              Description
            </label>
            <input
              id="list-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className="w-full rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1.5 text-[12px] text-stone-200 outline-none focus:border-amber-400/30"
            />
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.12em] text-stone-600">
              A contact is on this list when
            </span>
            <select
              value={match}
              aria-label="How the rules combine"
              onChange={(event) => setMatch(event.target.value as 'all' | 'any')}
              className="rounded border border-amber-400/15 bg-black/40 px-1.5 py-0.5 text-[10px] text-amber-200 outline-none"
            >
              <option value="all">every rule holds</option>
              <option value="any">any rule holds</option>
            </select>
          </div>

          <div className="space-y-1.5">
            {rules.map((rule, index) => (
              <div key={index} className="flex items-center gap-1.5">
                <select
                  value={rule.field}
                  aria-label={`Rule ${index + 1} field`}
                  onChange={(event) => setRule(index, { field: event.target.value })}
                  className="w-[34%] rounded-md border border-amber-400/10 bg-black/40 px-2 py-1.5 text-[11px] text-stone-200 outline-none focus:border-amber-400/30"
                >
                  {vocabulary.fields.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <select
                  value={rule.operator}
                  aria-label={`Rule ${index + 1} comparison`}
                  onChange={(event) => setRule(index, { operator: event.target.value })}
                  className="w-[26%] rounded-md border border-amber-400/10 bg-black/40 px-2 py-1.5 text-[11px] text-stone-200 outline-none focus:border-amber-400/30"
                >
                  {vocabulary.operators.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {needsValue.get(rule.operator) !== false ? (
                  <input
                    value={rule.value}
                    aria-label={`Rule ${index + 1} value`}
                    onChange={(event) => setRule(index, { value: event.target.value })}
                    placeholder="antarcticacapital.com"
                    className="min-w-0 flex-1 rounded-md border border-amber-400/10 bg-black/40 px-2 py-1.5 text-[11px] text-stone-200 outline-none placeholder:text-stone-600 focus:border-amber-400/30"
                  />
                ) : (
                  <span className="min-w-0 flex-1 px-2 text-[10px] text-stone-600">No value needed</span>
                )}
                <button
                  type="button"
                  aria-label={`Remove rule ${index + 1}`}
                  onClick={() => setRules((r) => r.filter((_, i) => i !== index))}
                  disabled={rules.length === 1}
                  className="flex-shrink-0 text-stone-600 hover:text-red-300 disabled:opacity-30"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setRules((r) => [...r, BLANK_RULE])}
            className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200"
          >
            <Plus size={10} /> Add a rule
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.12em] text-stone-600">Color</span>
          <div className="flex items-center gap-1" role="radiogroup" aria-label="Chip color">
            <button
              type="button"
              role="radio"
              aria-checked={colour === null}
              aria-label="No color"
              onClick={() => setColour(null)}
              className={clsx(
                'h-4 w-4 rounded-full border border-stone-600',
                colour === null && 'ring-1 ring-amber-300 ring-offset-1 ring-offset-black',
              )}
            />
            {LIST_COLOURS.map((c) => (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={colour === c.value}
                aria-label={c.label}
                onClick={() => setColour(c.value)}
                className={clsx(
                  'h-4 w-4 rounded-full',
                  c.className,
                  colour === c.value && 'ring-1 ring-amber-300 ring-offset-1 ring-offset-black',
                )}
              />
            ))}
          </div>
        </div>

        {/* The count, before it is saved. A list that matches nobody and one
            that has not been saved look identical without this. */}
        <div className="rounded border border-amber-400/10 bg-black/30 px-3 py-2 text-[11px]" aria-live="polite">
          {ready.length === 0 ? (
            <span className="text-stone-500">
              Finish a rule to see who it selects. A list with no rules is empty.
            </span>
          ) : preview ? (
            <span className={preview.total === 0 ? 'text-amber-200/80' : 'text-stone-300'}>
              Selects <span className="font-mono font-semibold">{preview.total}</span> of {preview.outOf}{' '}
              contacts.
              {preview.total === 0 && ' Nothing on record matches this yet.'}
            </span>
          ) : previewFailed ? (
            <span className="text-red-300">The preview could not be counted.</span>
          ) : (
            <span className="text-stone-500">Counting…</span>
          )}
        </div>

        {error ? <p className="text-[11px] text-red-300">{error}</p> : null}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-amber-400/10 bg-black/40 px-3 py-1 text-[11px] font-medium text-stone-300 transition-colors hover:text-amber-200">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="rounded-md border border-amber-400/20 bg-amber-400/[0.08] px-3 py-1 text-[11px] font-medium text-amber-200 transition-colors hover:bg-amber-400/[0.14] disabled:cursor-not-allowed disabled:text-stone-600"
          >
            {save.isPending ? 'Saving…' : list ? 'Save rules' : 'Create list'}
          </button>
        </div>
      </form>
    </DialogShell>
  )
}
